import React, { useRef, useMemo, useState } from "react";
import { Box, Typography, Paper, IconButton, Tooltip, Snackbar, Alert, Slide } from "@mui/material";
import VisibilityIcon from '@mui/icons-material/Visibility';
import GrievanceModal from './Modal';
import api from '../utils/apiInterceptor';
import { API_URL } from '../constants/data';

function SlideTransition(props) {
  return <Slide {...props} direction="left" />;
}

const UserExperienceFeed = ({ feedbackList = [], selectedRating, filters }) => {
  const containerRef = useRef(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoader, setModalLoader] = useState(false);
  const [feedbackData, setFeedbackData] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const handleViewDetails = async (docketNumber) => {
    try {
      setModalOpen(true);
      setModalLoader(true);
      setFeedbackData(null);

      // Call both APIs in parallel
      const [complaintRes, feedbackRes] = await Promise.allSettled([
        api.get(`${API_URL}/get_complaint_details`, {
          params: { complain_number: docketNumber }
        }),
        api.get(`${API_URL}/get_feedback_by_docket`, {
          params: { docket_number: parseInt(docketNumber) }
        })
      ]);

      // Handle complaint details
      if (complaintRes.status === 'fulfilled') {
        setModalData(complaintRes.value.data);
      } else {
        const error = complaintRes.reason;
        if (error.response?.status === 500 && error.response?.data?.detail?.includes("No complaint found")) {
          setSnackbarMessage(error.response.data.detail);
          setSnackbarOpen(true);
          setModalOpen(false);
          return;
        }
      }

      // Handle feedback data
      if (feedbackRes.status === 'fulfilled') {
        setFeedbackData(feedbackRes.value.data?.feedback || feedbackRes.value.data);
      }
      // Silently ignore feedback errors — it's optional
    } catch (error) {
      console.error('Error fetching details:', error);
    } finally {
      setModalLoader(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalData(null);
    setFeedbackData(null);
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  return (
    <>
      <Paper
        ref={containerRef}
        elevation={4}
        sx={{
          height: { xs: 320, sm: 400, md: 440, lg: 460, xl: 660 },
          minHeight: { xs: 320, sm: 380 },
          overflowY: "auto",
          backgroundColor: "#fff",
          color: "#000",
          p: { xs: 1.5, sm: 2 },
          borderRadius: 2,
          display: "flex",
          flexDirection: "column",
          boxShadow: "0px -3px 12px rgba(0, 0, 0, 0.06)",
        }}
      >
        {!feedbackList || feedbackList.length === 0 ? (
          <Typography
            variant="body2"
            sx={{ color: "#777", textAlign: "center", mt: "40%" }}
          >
            No feedback data available...
          </Typography>
        ) : (
          feedbackList.map((item, index) => (
            <Box
              key={item.id || index}
              sx={{
                mb: 1.5,
                p: 1.5,
                borderRadius: 1,
                backgroundColor: "#f6f7f9",
                border: "1px solid #e0e0e0",
                display: "flex",
                alignItems: "flex-start",
                gap: 1.5,
                transition: "all 0.2s ease",
                "&:hover": {
                  backgroundColor: "#ececec",
                  borderColor: "#bdbdbd",
                  transform: "translateX(4px)"
                },
              }}
            >
              {/* Remark Text */}
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#111",
                    lineHeight: 1.6,
                    wordBreak: "break-word",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {
                    [
                      item.userexperience && truncateText(item.userexperience),

                      item.ExperiencewithNCH &&
                      (item.ExperiencewithNCH.toLowerCase() === "unsatisfactory"
                        ? truncateText(item.unUnsatisfactory)
                        : truncateText(item.ExperiencewithNCH)),

                      item.Remark && truncateText(item.Remark, 150)
                    ]
                      .filter(Boolean)
                      .join(" | ")
                  }
                </Typography>

                {/* Docket Number */}
                {item.dockerNumber && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "#666",
                      fontSize: "0.7rem",
                      mt: 0.5,
                      display: "block",
                    }}
                  >
                    Docket: #{item.dockerNumber}
                  </Typography>
                )}
              </Box>

              {/* Eye Icon */}
              <Tooltip title="View Details" arrow>
                <IconButton
                  size="small"
                  onClick={() => handleViewDetails(item.Docketnumber)}
                  sx={{
                    color: "rgba(94, 79, 170, 1)",
                    flexShrink: 0,
                    "&:hover": {
                      backgroundColor: "rgba(25, 118, 210, 0.1)"
                    }
                  }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ))
        )}
      </Paper>

      {/* Modal */}
      <GrievanceModal
        open={modalOpen}
        onClose={handleCloseModal}
        modalData={modalData}
        modalLoader={modalLoader}
        feedbackData={feedbackData}
        selectedRating={selectedRating}
      />

      {/* Snackbar for errors */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        TransitionComponent={SlideTransition}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{
            width: '100%',
            backgroundColor: '#90CAF9',
            color: '#38474dff',
            borderRadius: '12px',
            boxShadow: '0 8px 24px rgba(0,0,0,0.15)',
            fontWeight: 500,
            '& .MuiAlert-icon': {
              color: '#ac3a06ff'
            },
            '& .MuiAlert-action': {
              color: '#000'
            }
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UserExperienceFeed;
